/**
 * GuidedFlow — 4-stage tabbed workflow for flagship experiments:
 * Learn → Practice → Observe → Report.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import LearnStage from "./LearnStage";
import PracticeStage from "./PracticeStage";
import ObserveStage from "./ObserveStage";
import ReportStage from "./ReportStage";
import { Sparkles, Hand, Beaker, FileCheck, Loader2 } from "lucide-react";

const STAGES = [
  { key: "learn", label: "Learn", icon: Sparkles, color: "text-cyan-400" },
  { key: "practice", label: "Practice", icon: Hand, color: "text-amber-400" },
  { key: "observe", label: "Observe", icon: Beaker, color: "text-emerald-400" },
  { key: "report", label: "Report", icon: FileCheck, color: "text-fuchsia-400" },
];

export const GuidedFlow = ({ experiment }) => {
  const [active, setActive] = useState("learn");
  const [stagesData, setStagesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState({ learn: false, practice: false, observe: false });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/experiments/${experiment.id}/stages`);
        setStagesData(data);
      } catch (err) {
        console.error("Failed to load stages:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [experiment.id]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center text-slate-500 font-mono">
        <Loader2 className="animate-spin mr-2 h-4 w-4" /> Loading guided flow…
      </div>
    );
  }

  if (!stagesData?.stages) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <Sparkles className="mx-auto h-10 w-10 text-cyan-400 mb-3" />
          <div className="text-slate-200">Guided mode coming soon for this experiment.</div>
        </div>
      </div>
    );
  }

  const cfg = stagesData.stages;

  const markComplete = (stage, next) => {
    setCompleted((c) => ({ ...c, [stage]: true }));
    if (next) setActive(next);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Stage tabs */}
      <div className="shrink-0 border-b border-slate-800 bg-slate-900/60 px-4 py-2 flex items-center gap-1 overflow-x-auto">
        {STAGES.map((s, i) => {
          const Icon = s.icon;
          const isDone = completed[s.key];
          const isActive = active === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setActive(s.key)}
              data-testid={`stage-tab-${s.key}`}
              className={`flex items-center gap-2 px-4 h-9 rounded-full text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap ${
                isActive
                  ? "bg-slate-800 border border-cyan-500/50 text-cyan-300"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              <Icon className={`h-3.5 w-3.5 ${isActive ? s.color : ""}`} />
              <span>{i + 1}. {s.label}</span>
              {isDone && <span className="text-emerald-400">✓</span>}
            </button>
          );
        })}
      </div>

      {/* Active stage content */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-2 sm:p-4">
        {active === "learn" && (
          <LearnStage
            experiment={experiment}
            steps={cfg.learn}
            onComplete={() => markComplete("learn", "practice")}
          />
        )}
        {active === "practice" && (
          <PracticeStage
            experiment={experiment}
            steps={cfg.practice}
            onComplete={() => markComplete("practice", "observe")}
          />
        )}
        {active === "observe" && (
          <ObserveStage
            experiment={experiment}
            observeCfg={cfg.observe}
            onComplete={() => markComplete("observe", "report")}
          />
        )}
        {active === "report" && (
          <ReportStage experiment={experiment} />
        )}
      </div>
    </div>
  );
};

export default GuidedFlow;
